<template>
  <!-- scorecard settings body (weights + conditional coloring), extracted from
       tenant_config_data_services.vue for FI-2047. host-agnostic: knows nothing
       about the modal chrome around it (ops_desk_settings contract). loads and
       saves its own two tenant-config keys with per-tab dirty-gated saves. -->
  <div class="dss-shell">
    <div v-if="loading" class="dss-loading"><b-spinner small class="mr-2" /> Loading settings...</div>
    <template v-else>
      <fi-tab-bar v-model="activeTab" :tabs="tabDefs" id-prefix="dss-tab" aria-label="Driver Manager settings sections" />
      <div class="dss-tabs-content">

        <!-- ===================== Combined Score Weights ===================== -->
        <section v-show="activeTab === 'weights'" role="tabpanel" aria-labelledby="dss-tab-weights">
          <div class="weights-panel">
            <div class="weights-panel-header">
              <span class="weights-panel-title">Combined Score Weights</span>
              <div class="d-flex align-items-center" style="gap:8px">
                <button type="button" class="weights-reset-btn" @click="resetWeightsToDefaults">Reset to defaults</button>
                <span class="weights-total-chip" :class="weightsTotalChipClass">{{ weightsTotal }} / 100</span>
              </div>
            </div>
            <div class="weights-grid">
              <div v-for="field in compiled_weights_fields" :key="field.key" class="weight-row">
                <span class="weight-metric-label">{{ field.label }}</span>
                <button type="button" class="weight-btn" @click="adjustWeight(field.key, -1)">&minus;</button>
                <input type="number" class="weight-num" v-model.number="compiled_weights_model[field.key]" min="0" max="100" step="1" />
                <button type="button" class="weight-btn" @click="adjustWeight(field.key, 1)">+</button>
                <span class="weight-pct">%</span>
                <div class="weight-bar-track">
                  <div class="weight-bar-fill" :style="{ width: barWidth(field.key) + '%' }"></div>
                </div>
              </div>
            </div>
            <p class="weights-hint" v-if="weightsTotal > 0 && weightsTotal !== 100">Weights are normalized proportionally, so they don't need to sum to 100.</p>
          </div>
          <div class="dss-actions">
            <span v-if="weightsDirty" class="dss-unsaved-hint">Unsaved changes</span>
            <span v-else-if="weightsSavedAt" class="dss-saved-hint">Saved</span>
            <b-button variant="primary" size="sm" :disabled="savingWeights || !weightsDirty" @click="saveWeights">
              <i class="fa fa-save"></i>&nbsp;<span v-if="savingWeights">Saving...</span><span v-else>Save</span>
            </b-button>
          </div>
          <div v-if="weightsError" class="dss-save-error">{{ weightsError }}</div>
        </section>

        <!-- ===================== Conditional Coloring ===================== -->
        <section v-show="activeTab === 'coloring'" role="tabpanel" aria-labelledby="dss-tab-coloring">
          <p class="thresholds-intro">
            Customize the boundaries that determine green / amber / red coloring on driver KPIs.
            Changes apply the next time driver data is loaded.
          </p>

          <!-- One card per active field -- tier drives which slider variant is rendered -->
          <div v-for="key in active_threshold_keys" :key="key" class="threshold-field-card">
            <div class="threshold-field-header">
              <span class="threshold-field-label">{{ fieldConfig(key).label }}</span>
              <span class="threshold-field-direction">
                {{ fieldConfig(key).direction === 'higher_better' ? 'higher is better' : 'lower is better' }}
                &middot; {{ tierCountFor(key) }} tiers
                <span v-if="isFlagMetricLockedAt3Tiers(key)" class="text-muted" title="This metric can flag critical drivers, so it can't be reduced further"> (locked)</span>
                <span v-if="fleetAverageDisplay(key)" class="threshold-fleet-avg" title="Current fleet average for this metric">&middot; Fleet avg: {{ fleetAverageDisplay(key) }}</span>
              </span>
              <button type="button" class="threshold-remove-btn" @click="removeField(key)" title="Remove field">&times;</button>
            </div>

            <!-- N-tier (2-5 bands): one handle per boundary, all sharing one track -->
            <template v-if="fieldConfig(key).tier !== '4'">
              <div class="dual-range-wrap" :style="sliderGradientStyle(key)">
                <input v-for="(b, idx) in thresholds_model[key].boundaries" :key="idx"
                  type="range" class="dual-range-input"
                  :min="fieldConfig(key).minVal || 0" :max="fieldConfig(key).scale" :step="fieldConfig(key).step"
                  :value="b"
                  @input="setBoundary(key, idx, $event.target.value)"
                  :style="{ zIndex: boundaryHandleZIndex(key, idx) }"
                />
              </div>
              <div class="slider-readout-row">
                <span v-for="(label, i) in tierLabels(thresholds_model[key].boundaries.length + 1)" :key="'group-' + i" class="readout-group">
                  <span class="readout-tier" :class="tierClass(i, thresholds_model[key].boundaries.length + 1)">{{ label }}</span>
                  <template v-if="i < thresholds_model[key].boundaries.length">
                    <span class="readout-sep">{{ fieldConfig(key).direction === 'higher_better' ? '≥' : '≤' }}</span>
                    <input type="number" class="threshold-num" :class="{ 'threshold-input-error': thresholdErrors[key] }"
                      :value="thresholds_model[key].boundaries[i]"
                      @input="setBoundary(key, i, $event.target.value)"
                      :step="fieldConfig(key).step" :min="fieldConfig(key).minVal || 0" :max="fieldConfig(key).scale" />
                  </template>
                </span>
                <span class="readout-tier-buttons">
                  <button type="button" class="tier-add-btn" title="Add a tier" v-if="thresholds_model[key].boundaries.length < 4" @click="addTier(key)">+ Tier</button>
                  <button type="button" class="tier-remove-btn" title="Remove a tier" v-if="thresholds_model[key].boundaries.length > 1" @click="removeTier(key)">&minus; Tier</button>
                </span>
              </div>
            </template>

            <!-- 4-tier: triple-handle (compiled score), fixed cutoffs -- the default -->
            <template v-else>
              <div class="dual-range-wrap" :style="sliderGradientStyle(key)">
                <input type="range" class="dual-range-input" min="0" max="100" step="1"
                  v-model.number="thresholds_model[key].coaching"
                  :style="{ zIndex: compiledHandleZIndex('coaching') }"
                />
                <input type="range" class="dual-range-input" min="0" max="100" step="1"
                  v-model.number="thresholds_model[key].solid"
                  :style="{ zIndex: compiledHandleZIndex('solid') }"
                />
                <input type="range" class="dual-range-input" min="0" max="100" step="1"
                  v-model.number="thresholds_model[key].elite"
                  :style="{ zIndex: compiledHandleZIndex('elite') }"
                />
              </div>
              <div class="slider-readout-row compiled-readout-row">
                <div class="readout-end readout-left">
                  <span class="tier-bad readout-tier">High Risk</span>
                  <span class="readout-sep">&lt;</span>
                  <input type="number" class="threshold-num" :class="{ 'threshold-input-error': thresholdErrors[key] }"
                    v-model.number="thresholds_model[key].coaching" step="1" min="0" max="100" />
                  <span class="readout-unit">Fair</span>
                </div>
                <div class="readout-end readout-center">
                  <span class="readout-unit">Solid</span>
                  <input type="number" class="threshold-num" :class="{ 'threshold-input-error': thresholdErrors[key] }"
                    v-model.number="thresholds_model[key].solid" step="1" min="0" max="100" />
                </div>
                <div class="readout-end readout-right">
                  <span class="readout-unit">Elite</span>
                  <input type="number" class="threshold-num" :class="{ 'threshold-input-error': thresholdErrors[key] }"
                    v-model.number="thresholds_model[key].elite" step="1" min="0" max="100" />
                  <span class="readout-sep">&le;</span>
                  <span class="tier-elite readout-tier">Elite</span>
                </div>
              </div>
            </template>

            <div v-if="thresholdErrors[key]" class="threshold-error-msg">{{ thresholdErrors[key] }}</div>
          </div>

          <!-- Add optional field -->
          <div class="threshold-add-row">
            <b-form-select v-model="field_to_add" :options="addableFieldOptions" style="width:auto; min-width:240px; font-size:13px;" />
            <b-btn variant="outline-primary" size="sm" :disabled="!field_to_add" @click="addField">+ Add Field</b-btn>
          </div>

          <div class="dss-actions dss-actions--split">
            <button type="button" class="weights-reset-btn" @click="resetThresholdsToDefaults">Reset to Defaults</button>
            <div class="dss-actions-primary">
              <span v-if="coloringDirty" class="dss-unsaved-hint">Unsaved changes</span>
              <span v-else-if="coloringSavedAt" class="dss-saved-hint">Saved</span>
              <b-button variant="primary" size="sm" :disabled="hasThresholdErrors || savingColoring || !coloringDirty" @click="saveColoring">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingColoring">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="coloringError" class="dss-save-error">{{ coloringError }}</div>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { get_tenant_config_settings, update_tenant_config_settings, cm_get_all_driver_scorecards } from '../networking/apis'
import { get_all_driver_scorecards } from '../networking/tbn_apis'
import { DEFAULT_SCORECARD_THRESHOLDS, OPTIONAL_SCORECARD_DEFAULTS, THRESHOLD_FIELD_CONFIG, DEFAULT_THRESHOLD_KEYS, FLAG_METRIC_THRESHOLD_KEYS } from '../utils/driver_manager_thresholds'
import FiTabBar from './fi_tab_bar.vue'

// Maps a threshold key onto the /driver-scorecard/all response field it should read the
// fleet average from -- most keys share the same name, distracted_driving_percentage is
// the one exception (see FLAG_METRICS' dataKey in driver_manager_dashboard.vue).
const THRESHOLD_KEY_TO_FLEET_AVG_FIELD = {
  distracted_driving_percentage: 'distracted_driving_per_10k_miles',
}

// CM scorecards have no dispatch-derived customer_requests / weekend_availability
// concepts (dispatch comes from BOOKING_TABLE, not TBN) -- hide them everywhere
// in this settings UI for CM tenants so admins can't configure fields that will
// never have data.
const TBN_ONLY_SCORECARD_FIELDS = ['weekend_availability_percentage', 'customer_requests_per_10k_miles', 'customer_requests']

// Best->worst color/label/class sets keyed by band count, kept in sync with the
// BAND_PALETTES in ../utils/driver_manager_thresholds.js so the slider preview matches the
// actual scorecard coloring (text-success/info/primary/warning/danger hex equivalents).
const GRADIENT_COLORS = {
  2: ['#28a745', '#ffc107'],
  3: ['#28a745', '#ffc107', '#dc3545'],
  4: ['#28a745', '#17a2b8', '#ffc107', '#dc3545'],
  5: ['#28a745', '#17a2b8', '#007bff', '#ffc107', '#dc3545'],
}
const TIER_LABEL_SETS = {
  2: ['Good', 'Warning'],
  3: ['Good', 'Warning', 'Bad'],
  4: ['Great', 'Good', 'Warning', 'Bad'],
  5: ['Great', 'Good', 'OK', 'Warning', 'Bad'],
}
// Index 1 of the 4-/5-band sets is rendered with GRADIENT_COLORS' teal (#17a2b8, an
// "info" shade), not green -- 'tier-info' keeps the readout text color aligned with the
// slider gradient at that stop instead of reusing the green 'tier-good' label.
const TIER_CLASS_SETS = {
  2: ['tier-good', 'tier-warning'],
  3: ['tier-good', 'tier-warning', 'tier-bad'],
  4: ['tier-great', 'tier-info', 'tier-warning', 'tier-bad'],
  5: ['tier-great', 'tier-info', 'tier-ok', 'tier-warning', 'tier-bad'],
}

// Build a boundaries array (best->worst per direction) from a legacy good/warning pair,
// falling back to `defaults`' good/warning for whichever of the two is missing from `src`
// (e.g. a stored 3-tier field carrying only {good: 80} shouldn't silently collapse to a
// 2-band config just because 'warning' wasn't part of that particular save).
function boundariesFromLegacy(src, defaults = {}) {
  const good = src.good !== undefined && src.good !== null ? src.good : defaults.good
  const warning = src.warning !== undefined && src.warning !== null ? src.warning : defaults.warning
  const b = []
  if (good !== undefined && good !== null) b.push(Number(good))
  if (warning !== undefined && warning !== null) b.push(Number(warning))
  return b
}

// Normalize any saved field shape (new `boundaries` array, or legacy `good`/`warning`)
// into the boundaries-based shape this editor edits. compiled_score keeps its own shape.
// `defaults` (that field's default config) fills in any keys missing from a partial
// stored config -- e.g. a stored compiled_score of {elite: 90, solid: 70} would otherwise
// yield coaching: undefined and brick Save, and a 3-tier field carrying only {good: 80}
// would silently collapse to a 2-band config on the next save.
function normalizeFieldModel(cfg, src, defaults = {}) {
  if (cfg.tier === '4') {
    return {
      elite: src.elite !== undefined ? src.elite : defaults.elite,
      solid: src.solid !== undefined ? src.solid : defaults.solid,
      coaching: src.coaching !== undefined ? src.coaching : defaults.coaching,
    }
  }
  if (Array.isArray(src.boundaries) && src.boundaries.length) {
    return { boundaries: src.boundaries.map(Number).filter(n => !isNaN(n)).slice(0, 4) }
  }
  return { boundaries: boundariesFromLegacy(src, defaults) }
}

function buildDefaultThresholdsModel() {
  const model = {}
  for (const [key, cfg] of Object.entries(THRESHOLD_FIELD_CONFIG)) {
    const src = DEFAULT_SCORECARD_THRESHOLDS[key] || OPTIONAL_SCORECARD_DEFAULTS[key] || {}
    model[key] = normalizeFieldModel(cfg, src, src)
  }
  return model
}

const SETTINGS_TABS = ['weights', 'coloring']

export default {
  name: 'driver_manager_settings',
  components: { FiTabBar },
  props: {
    initialTab: { type: String, default: 'weights' },
  },

  data() {
    return {
      activeTab: SETTINGS_TABS.includes(this.initialTab) ? this.initialTab : 'weights',
      loading: true,
      // weights tab
      all_compiled_weights_fields: [
        { key: 'risk_score',                        label: 'Safety Score' },
        { key: 'speeding_percentage',               label: 'Speeding %' },
        { key: 'idling_percentage',                 label: 'Idling %' },
        { key: 'following_distance_per_10k_miles',  label: 'Following Distance / 10k' },
        { key: 'harsh_braking_per_10k_miles',       label: 'Harsh Braking / 10k' },
        { key: 'distracted_driving_percentage',     label: 'Distracted Driving / 10k' },
        { key: 'weekend_availability_percentage',   label: 'Weekend Availability %' },
        { key: 'customer_requests_per_10k_miles',   label: 'Customer Requests / 10k' },
        { key: 'dvir_completion_rate',              label: 'DVIR Completion %' },
      ],
      compiled_weights_model: {
        risk_score: 35,
        speeding_percentage: 10,
        idling_percentage: 15,
        following_distance_per_10k_miles: 5,
        harsh_braking_per_10k_miles: 5,
        distracted_driving_percentage: 5,
        weekend_availability_percentage: 10,
        customer_requests_per_10k_miles: 10,
        dvir_completion_rate: 5,
      },
      savingWeights: false,
      weightsError: null,
      weightsSavedAt: null,
      weightsSnapshot: null,
      // coloring tab
      active_threshold_keys: [...DEFAULT_THRESHOLD_KEYS],
      thresholds_model: buildDefaultThresholdsModel(),
      field_to_add: null,
      savingColoring: false,
      coloringError: null,
      coloringSavedAt: null,
      coloringSnapshot: null,
      fleet_average_thresholds: null,
      fleet_average_thresholds_loading: false,
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant_type() {
      return this.user.types[this.user.tenant]
    },
    tabDefs() {
      return [
        { id: 'weights', label: 'Combined Score Weights', dot: this.weightsDirty },
        { id: 'coloring', label: 'Conditional Coloring', dot: this.coloringDirty },
      ]
    },
    compiled_weights_fields() {
      if (this.tenant_type === 'CM') {
        return this.all_compiled_weights_fields.filter(f => !TBN_ONLY_SCORECARD_FIELDS.includes(f.key))
      }
      return this.all_compiled_weights_fields
    },
    weightsTotal() {
      return this.compiled_weights_fields.reduce((sum, f) => sum + (Number(this.compiled_weights_model[f.key]) || 0), 0)
    },
    weightsTotalChipClass() {
      const t = this.weightsTotal
      if (t === 0) return 'chip-neutral'
      if (t === 100) return 'chip-success'
      return 'chip-warning'
    },
    weightsPayload() {
      const obj = {}
      for (const f of this.compiled_weights_fields) {
        const v = Number(this.compiled_weights_model[f.key]) || 0
        if (v > 0) obj[f.key] = v
      }
      return obj
    },
    weightsDirty() {
      return this.weightsSnapshot !== null && JSON.stringify(this.weightsPayload) !== this.weightsSnapshot
    },
    coloringPayload() {
      const out = {}
      for (const key of this.active_threshold_keys) {
        const m = this.thresholds_model[key]
        if (m) out[key] = this._serializeThresholdField(key, m)
      }
      return out
    },
    coloringDirty() {
      return this.coloringSnapshot !== null && JSON.stringify(this.coloringPayload) !== this.coloringSnapshot
    },
    thresholdErrors() {
      const errors = {}
      for (const key of this.active_threshold_keys) {
        const cfg = THRESHOLD_FIELD_CONFIG[key]
        const m   = this.thresholds_model[key]
        if (!cfg || !m) continue

        if (cfg.tier === '4') {
          const e = Number(m.elite), s = Number(m.solid), c = Number(m.coaching)
          if (isNaN(e) || isNaN(s) || isNaN(c)) {
            errors[key] = 'Values must be numbers'
          } else if (!(e > s && s > c && c >= 0)) {
            errors[key] = 'Tiers must be in descending order: Elite > Solid > Fair ≥ 0'
          }
        } else {
          // N-tier (2-5 bands): boundaries must be finite, within [minVal, scale], and
          // strictly ordered best->worst per direction (descending for higher_better,
          // ascending for lower_better).
          const minVal = cfg.minVal || 0
          const scale = cfg.scale
          const boundaries = (m.boundaries || []).map(Number)
          if (!boundaries.length || boundaries.some(b => isNaN(b))) {
            errors[key] = 'Values must be numbers'
          } else if (boundaries.some(b => b < minVal || b > scale)) {
            errors[key] = `Values must be between ${minVal} and ${scale}`
          } else {
            let ordered = true
            for (let i = 1; i < boundaries.length; i++) {
              const stepOk = cfg.direction === 'higher_better'
                ? boundaries[i - 1] > boundaries[i]
                : boundaries[i - 1] < boundaries[i]
              if (!stepOk) ordered = false
            }
            if (!ordered) {
              errors[key] = cfg.direction === 'higher_better'
                ? 'Tiers must be in descending order (best tier first)'
                : 'Tiers must be in ascending order (best tier first)'
            }
          }
        }
      }
      return errors
    },
    hasThresholdErrors() {
      return Object.keys(this.thresholdErrors).length > 0
    },
    addableFieldOptions() {
      const opts = [{ value: null, text: 'Select a field to add...' }]
      const active = new Set(this.active_threshold_keys)
      const isCm = this.tenant_type === 'CM'
      for (const key of Object.keys(THRESHOLD_FIELD_CONFIG)) {
        if (isCm && TBN_ONLY_SCORECARD_FIELDS.includes(key)) continue
        if (!active.has(key)) {
          opts.push({ value: key, text: THRESHOLD_FIELD_CONFIG[key].label })
        }
      }
      return opts
    },
  },

  watch: {
    activeTab(id) {
      if (id === 'coloring') this.loadFleetAverageForThresholds()
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        const { value: { data = {} } } = await get_tenant_config_settings(this.user.tenant)
        const model = (data && data.results) || {}
        this.hydrateWeights(model.scorecard_compiled_weights)
        this.hydrateColoring(model.scorecard_thresholds)
      } catch (e) {
        // leave the defaults in place; saves still work (they PATCH one key)
        this.weightsSnapshot = JSON.stringify(this.weightsPayload)
        this.coloringSnapshot = JSON.stringify(this.coloringPayload)
      }
      this.loading = false
      if (this.activeTab === 'coloring') this.loadFleetAverageForThresholds()
    },

    hydrateWeights(raw) {
      try {
        const parsed = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null
        if (parsed && Object.keys(parsed).length > 0) {
          for (const f of this.compiled_weights_fields) {
            this.$set(this.compiled_weights_model, f.key, Number(parsed[f.key]) || 0)
          }
        }
        // no saved weights: compiled_weights_model stays at the data() defaults
      } catch (e) {
        // leave defaults in place on parse error
      }
      this.weightsSnapshot = JSON.stringify(this.weightsPayload)
    },

    hydrateColoring(raw) {
      const isCm = this.tenant_type === 'CM'
      this.$set(this, 'thresholds_model', buildDefaultThresholdsModel())
      try {
        const saved = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null
        if (saved !== null && saved !== undefined) {
          // Tenant has explicitly saved before (even if they saved zero fields) -- honour it
          for (const key of Object.keys(this.thresholds_model)) {
            if (saved[key] && typeof saved[key] === 'object') {
              const cfg = THRESHOLD_FIELD_CONFIG[key] || {}
              const defaults = DEFAULT_SCORECARD_THRESHOLDS[key] || OPTIONAL_SCORECARD_DEFAULTS[key] || {}
              this.$set(this.thresholds_model, key, normalizeFieldModel(cfg, saved[key], defaults))
            }
          }
          // Active keys = only what the tenant explicitly saved, in canonical display order
          const ALL_KEYS = Object.keys(THRESHOLD_FIELD_CONFIG)
          let keys = ALL_KEYS.filter(k => k in saved)
          if (isCm) keys = keys.filter(k => !TBN_ONLY_SCORECARD_FIELDS.includes(k))
          this.$set(this, 'active_threshold_keys', keys)
        } else {
          // Never configured -- show all defaults as a starting point
          this.$set(this, 'active_threshold_keys', this._defaultThresholdKeysForTenant())
        }
      } catch (e) {
        // leave defaults on parse error
        this.$set(this, 'active_threshold_keys', this._defaultThresholdKeysForTenant())
      }
      this.field_to_add = null
      this.coloringSnapshot = JSON.stringify(this.coloringPayload)
    },

    // --- Compiled weights ---
    adjustWeight(key, delta) {
      const current = Number(this.compiled_weights_model[key]) || 0
      this.$set(this.compiled_weights_model, key, Math.min(100, Math.max(0, current + delta)))
    },
    resetWeightsToDefaults() {
      // Mirrors DriverScorecardProcessor.DEFAULT_COMPILED_WEIGHTS (tbn-integration)
      const tbn_defaults = { risk_score: 35, idling_percentage: 15, speeding_percentage: 10, following_distance_per_10k_miles: 5, harsh_braking_per_10k_miles: 5, weekend_availability_percentage: 10, customer_requests_per_10k_miles: 10, distracted_driving_percentage: 5, dvir_completion_rate: 5 }
      // Mirrors CMScorecardProcessor.DEFAULT_COMPILED_WEIGHTS (fleet-int-api) -- no
      // weekend_availability / customer_requests, redistributed to remaining metrics
      const cm_defaults = { risk_score: 35, idling_percentage: 20, speeding_percentage: 15, following_distance_per_10k_miles: 8, harsh_braking_per_10k_miles: 5, distracted_driving_percentage: 7, dvir_completion_rate: 10 }
      const defaults = this.tenant_type === 'CM' ? cm_defaults : tbn_defaults
      for (const f of this.compiled_weights_fields) {
        this.$set(this.compiled_weights_model, f.key, defaults[f.key] || 0)
      }
    },
    barWidth(key) {
      const total = this.weightsTotal
      if (total === 0) return 0
      return Math.round(((Number(this.compiled_weights_model[key]) || 0) / total) * 100)
    },
    async saveWeights() {
      if (this.savingWeights || !this.weightsDirty) return
      this.savingWeights = true
      this.weightsError = null
      try {
        const payload = this.weightsPayload
        await update_tenant_config_settings(this.user.tenant, {
          scorecard_compiled_weights: Object.keys(payload).length ? payload : '',
        })
        this.weightsSnapshot = JSON.stringify(payload)
        this.weightsSavedAt = new Date()
        this.$emit('saved', 'weights')
      } catch (e) {
        this.weightsSavedAt = null
        this.weightsError = 'Error saving weights. Check your connection and try again.'
      } finally {
        this.savingWeights = false
      }
    },

    // --- Conditional coloring ---
    _defaultThresholdKeysForTenant() {
      if (this.tenant_type === 'CM') {
        return DEFAULT_THRESHOLD_KEYS.filter(k => !TBN_ONLY_SCORECARD_FIELDS.includes(k))
      }
      return [...DEFAULT_THRESHOLD_KEYS]
    },
    async loadFleetAverageForThresholds() {
      if (this._fleetAvgRequested) return
      this._fleetAvgRequested = true
      this.fleet_average_thresholds = null
      this.fleet_average_thresholds_loading = true
      try {
        const end_month = moment().subtract(1, 'month').format('YYYY-MM')
        const start_month = moment().subtract(6, 'months').format('YYYY-MM')
        const isCm = this.tenant_type === 'CM'
        const response = isCm
          ? await cm_get_all_driver_scorecards(this.user.tenant, start_month, end_month)
          : await get_all_driver_scorecards(this.user.tenant, start_month, end_month)
        const responseData = (response && response.value && response.value.data) || (response && response.data)
        this.fleet_average_thresholds = (responseData && !responseData.error && responseData.fleet_average) || null
      } catch (e) {
        this.fleet_average_thresholds = null
      } finally {
        this.fleet_average_thresholds_loading = false
      }
    },
    // Shown next to each metric's slider so an admin can see roughly where the fleet
    // sits today before dragging a boundary -- display-only, doesn't affect saving.
    fleetAverageDisplay(key) {
      if (!this.fleet_average_thresholds) return null
      const dataKey = THRESHOLD_KEY_TO_FLEET_AVG_FIELD[key] || key
      const value = this.fleet_average_thresholds[dataKey]
      if (value == null || isNaN(Number(value))) return null
      return Number(value).toFixed(2)
    },
    resetThresholdsToDefaults() {
      this.$set(this, 'active_threshold_keys', this._defaultThresholdKeysForTenant())
      this.field_to_add = null
      this.$set(this, 'thresholds_model', buildDefaultThresholdsModel())
    },
    async saveColoring() {
      if (this.hasThresholdErrors || this.savingColoring || !this.coloringDirty) return
      this.savingColoring = true
      this.coloringError = null
      try {
        const out = this.coloringPayload
        await update_tenant_config_settings(this.user.tenant, { scorecard_thresholds: out })
        this.coloringSnapshot = JSON.stringify(out)
        this.coloringSavedAt = new Date()
        this.$emit('saved', 'coloring')
      } catch (e) {
        this.coloringSavedAt = null
        this.coloringError = 'Error saving thresholds. Check your connection and try again.'
      } finally {
        this.savingColoring = false
      }
    },
    fieldConfig(key) {
      return THRESHOLD_FIELD_CONFIG[key] || {}
    },
    // The live editor model always edits non-compiled-score fields as a `boundaries`
    // array, but persisting that shape unconditionally would silently switch every
    // 2- and 3-band field (the pre-existing default for all of them) from the legacy
    // good/warning band-interpolation formula to the new N-band formula on the very
    // first save, re-scoring the whole fleet with no real config change. Only fields
    // an admin has genuinely re-tiered past 3 bands (via "+ Tier") should persist as
    // `boundaries`; everything else round-trips back to good/warning.
    _serializeThresholdField(key, model) {
      const cfg = THRESHOLD_FIELD_CONFIG[key] || {}
      if (cfg.tier === '4' || !Array.isArray(model.boundaries)) {
        return model
      }
      const boundaries = model.boundaries
      if (boundaries.length <= 2) {
        const legacy = { good: boundaries[0] }
        if (boundaries.length === 2) legacy.warning = boundaries[1]
        return legacy
      }
      return { boundaries }
    },
    addField() {
      const key = this.field_to_add
      // Belt-and-suspenders: bail if empty, unknown, already active, or a
      // TBN-only field on a CM tenant -- the dropdown already excludes these,
      // but never trust the model alone.
      const isCmBlocked = this.tenant_type === 'CM' && TBN_ONLY_SCORECARD_FIELDS.includes(key)
      if (!key || !THRESHOLD_FIELD_CONFIG[key] || this.active_threshold_keys.includes(key) || isCmBlocked) {
        this.field_to_add = null
        return
      }
      this.active_threshold_keys = [...new Set([...this.active_threshold_keys, key])]
      this.field_to_add = null
    },
    removeField(key) {
      const idx = this.active_threshold_keys.indexOf(key)
      if (idx !== -1) this.active_threshold_keys.splice(idx, 1)
    },
    sliderGradientStyle(key) {
      const cfg = THRESHOLD_FIELD_CONFIG[key]
      if (!cfg) return {}
      const m = this.thresholds_model[key]
      if (!m) return {}
      const scale = cfg.scale || 100

      if (cfg.tier === '4') {
        const elite    = Math.min(Math.max(Number(m.elite)    || 80, 0), 100)
        const solid    = Math.min(Math.max(Number(m.solid)    || 60, 0), 100)
        const coaching = Math.min(Math.max(Number(m.coaching) || 35, 0), 100)
        return { background: `linear-gradient(to right, #dc3545 0%, #dc3545 ${coaching}%, #ffc107 ${coaching}%, #ffc107 ${solid}%, #007bff ${solid}%, #007bff ${elite}%, #28a745 ${elite}%, #28a745 100%)` }
      }

      // N-tier (2-5 bands): build the gradient from N-1 boundary positions on the 0-scale
      // track. Colors are ordered best->worst; since the track's left-right axis is plain
      // ascending value (not "best/worst"), which color goes on which physical side flips
      // with direction -- lower_better puts best (low values) on the left, higher_better
      // puts best (high values) on the right.
      const boundaries = (m.boundaries || []).map(Number).filter(n => !isNaN(n))
      if (!boundaries.length) return {}
      const bandCount = Math.min(boundaries.length + 1, 5)
      const colors = GRADIENT_COLORS[bandCount] || GRADIENT_COLORS[3]
      const minVal = cfg.minVal || 0
      const ascendingPct = boundaries
        .map(b => (Math.min(Math.max(Number(b), minVal), scale) / scale) * 100)
        .sort((a, b) => a - b)
      const edges = [0, ...ascendingPct, 100]
      const stops = []
      for (let i = 0; i < edges.length - 1; i++) {
        const color = cfg.direction === 'higher_better' ? colors[bandCount - 1 - i] : colors[i]
        stops.push(`${color} ${edges[i]}%`, `${color} ${edges[i + 1]}%`)
      }
      return { background: `linear-gradient(to right, ${stops.join(', ')})` }
    },
    compiledHandleZIndex(tier) {
      const cs = this.thresholds_model.compiled_score
      if (!cs) return 3
      const values = { coaching: Number(cs.coaching) || 0, solid: Number(cs.solid) || 0, elite: Number(cs.elite) || 0 }
      const sorted = Object.entries(values).sort((a, b) => a[1] - b[1]).map(e => e[0])
      return sorted.indexOf(tier) + 3
    },
    // Handles overlap on the shared track; rank by value so whichever handle sits at an
    // extreme is always reachable (same trick as compiledHandleZIndex, generalized to N).
    boundaryHandleZIndex(key, idx) {
      const m = this.thresholds_model[key]
      if (!m || !Array.isArray(m.boundaries)) return 3
      const values = m.boundaries.map(Number)
      const order = values.map((v, i) => i).sort((a, b) => values[a] - values[b])
      return order.indexOf(idx) + 3
    },
    setBoundary(key, idx, value) {
      const m = this.thresholds_model[key]
      if (!m || !Array.isArray(m.boundaries)) return
      this.$set(m.boundaries, idx, Number(value))
    },
    addTier(key) {
      const cfg = THRESHOLD_FIELD_CONFIG[key]
      const m = this.thresholds_model[key]
      if (!cfg || cfg.tier === '4' || !m || !Array.isArray(m.boundaries) || m.boundaries.length >= 4) return
      const scale = cfg.scale || 100
      const minVal = cfg.minVal || 0
      const boundaries = [...m.boundaries]
      const worstEdge = cfg.direction === 'higher_better' ? minVal : scale
      const last = boundaries.length ? boundaries[boundaries.length - 1] : worstEdge
      // New boundary splits the gap between the current worst tier's boundary and the
      // track's far edge -- the admin can drag it anywhere afterward.
      const mid = Number(((last + worstEdge) / 2).toFixed(2))
      if (Math.abs(mid - last) < (cfg.step || 1)) return  // no room for another tier
      boundaries.push(mid)
      boundaries.sort((a, b) => cfg.direction === 'higher_better' ? b - a : a - b)
      this.$set(m, 'boundaries', boundaries)
    },
    removeTier(key) {
      const cfg = THRESHOLD_FIELD_CONFIG[key]
      const m = this.thresholds_model[key]
      if (!cfg || cfg.tier === '4' || !m || !Array.isArray(m.boundaries) || m.boundaries.length <= 1) return
      // Metrics used for critical/warning flagging on the Flagged Drivers tab need at
      // least 3 tiers, since the 2-band palette's worst class caps at 'text-warning' and
      // can never reach 'text-danger' -- dropping below 3 would silently and permanently
      // disable that metric's ability to ever flag critical.
      if (m.boundaries.length <= 2 && FLAG_METRIC_THRESHOLD_KEYS.includes(key)) return
      const boundaries = [...m.boundaries]
      boundaries.pop()
      this.$set(m, 'boundaries', boundaries)
    },
    isFlagMetricLockedAt3Tiers(key) {
      // Only show the note once the field is actually at the floor removeTier enforces
      // (3 tiers). At 2 tiers (e.g. customer_requests_per_10k_miles's default shape) the
      // remove button isn't even rendered, so there's nothing being "locked" there yet.
      return FLAG_METRIC_THRESHOLD_KEYS.includes(key) && this.tierCountFor(key) === 3
    },
    tierCountFor(key) {
      const cfg = THRESHOLD_FIELD_CONFIG[key]
      if (!cfg) return 0
      if (cfg.tier === '4') return 4
      const m = this.thresholds_model[key]
      return ((m && m.boundaries) || []).length + 1
    },
    tierLabels(bandCount) {
      return TIER_LABEL_SETS[bandCount] || TIER_LABEL_SETS[3]
    },
    tierClass(idx, bandCount) {
      const classes = TIER_CLASS_SETS[bandCount] || TIER_CLASS_SETS[3]
      return classes[idx] || ''
    },
  },
}
</script>

<style scoped>
/* the global #app { text-align: center } cascade would otherwise center
   everything in the modal (ops_desk_settings precedent) */
.dss-shell { text-align: left; }
.dss-loading { padding: 24px 8px; color: #6c757d; font-size: 14px; display: flex; align-items: center; }
.dss-tabs-content { padding-top: 16px; }
.dss-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eef0f3;
}
.dss-actions--split { justify-content: space-between; }
.dss-actions-primary { display: flex; align-items: center; gap: 10px; }
.dss-unsaved-hint { font-size: 12px; color: #b58100; font-weight: 600; }
.dss-saved-hint { font-size: 12px; color: #3a8144; font-weight: 600; }
.dss-save-error { margin-top: 8px; font-size: 12.5px; color: #dc3545; text-align: right; }

/* ========== Compiled Weights Panel ========== */
.weights-panel {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px 28px;
  background: #fff;
  max-width: 860px;
  margin: 4px auto;
}
.weights-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}
.weights-panel-title { font-size: 15px; font-weight: 600; color: #333; }
.weights-total-chip { font-size: 13px; font-weight: 700; padding: 3px 13px; border-radius: 20px; }
.chip-neutral { background: #e9ecef; color: #666; }
.chip-success { background: #d4edda; color: #155724; }
.chip-warning { background: #fff3cd; color: #856404; }
.weights-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 40px; margin-bottom: 10px; }
.weight-row { display: flex; align-items: center; gap: 7px; }
.weight-metric-label { flex: 0 0 155px; font-size: 13px; color: #555; text-align: right; white-space: nowrap; }
.weight-btn { flex-shrink: 0; width: 26px; height: 26px; border: 1px solid #ced4da; background: #f8f9fa; border-radius: 4px; font-size: 15px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; color: #333; }
.weight-btn:hover { background: #e2e6ea; }
.weight-btn:active { background: #d6d9dd; }
.weight-num { width: 50px; text-align: center; border: 1px solid #ced4da; border-radius: 4px; padding: 3px 4px; font-size: 14px; -moz-appearance: textfield; }
.weight-num::-webkit-inner-spin-button, .weight-num::-webkit-outer-spin-button { -webkit-appearance: none; }
.weight-pct { flex-shrink: 0; font-size: 12px; color: #999; }
.weight-bar-track { flex: 1; height: 5px; background: #e9ecef; border-radius: 3px; overflow: hidden; min-width: 30px; }
.weight-bar-fill { height: 100%; background: steelblue; border-radius: 3px; transition: width 0.15s ease; }
.weights-hint { font-size: 12px; color: #aaa; font-style: italic; margin: 6px 0 0 0; }
.weights-reset-btn { font-size: 12px; padding: 3px 10px; border: 1px solid #ced4da; background: #f8f9fa; border-radius: 4px; cursor: pointer; color: #555; }
.weights-reset-btn:hover { background: #e2e6ea; }

/* ========== Conditional Coloring ========== */
.thresholds-intro { font-size: 13px; color: #666; margin-bottom: 16px; }
.threshold-field-card { border: 1px solid #e9ecef; border-radius: 8px; padding: 14px 18px; margin-bottom: 12px; background: #fafafa; }
.threshold-field-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.threshold-field-label { font-size: 14px; font-weight: 600; color: #333; }
.threshold-field-direction { font-size: 12px; color: #888; font-style: italic; }
.threshold-fleet-avg { color: #0c5460; font-style: normal; }
.threshold-num { width: 68px; text-align: center; border: 1px solid #ced4da; border-radius: 4px; padding: 3px 6px; font-size: 14px; -moz-appearance: textfield; }
.threshold-num::-webkit-inner-spin-button, .threshold-num::-webkit-outer-spin-button { -webkit-appearance: none; }
.threshold-input-error { border-color: #dc3545 !important; background: #fff5f5; }
.threshold-error-msg { font-size: 12px; color: #dc3545; margin-bottom: 6px; }
.tier-good     { color: #155724; }
.tier-warning  { color: #856404; }
.tier-bad      { color: #721c24; }
.tier-great    { color: #0f5132; }
.tier-ok       { color: #004085; }
.tier-info     { color: #0c5460; }
.tier-solid    { color: #004085; }
.tier-coaching { color: #856404; }
.tier-elite    { color: #155724; font-weight: 700; }

.tier-add-btn, .tier-remove-btn {
  border: 1px solid #ced4da;
  background: #fff;
  border-radius: 4px;
  font-size: 11px;
  padding: 2px 8px;
  margin-left: 6px;
  cursor: pointer;
  color: #495057;
}
.tier-add-btn:hover, .tier-remove-btn:hover { background: #f1f3f5; }

/* Remove button on optional field cards */
.threshold-remove-btn {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  color: #999;
  cursor: pointer;
  padding: 0 4px;
  transition: color 0.15s;
}
.threshold-remove-btn:hover { color: #dc3545; }

/* Add Field row */
.threshold-add-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0 4px;
  padding: 10px 14px;
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  border-radius: 8px;
}

/* ========== Dual-handle gradient slider ========== */
.dual-range-wrap {
  position: relative;
  height: 32px;
  border-radius: 6px;
  margin: 10px 0 4px;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.2);
}
.dual-range-input {
  position: absolute;
  left: 0;
  width: 100%;
  height: 32px;
  margin: 0;
  padding: 0;
  background: transparent;
  -webkit-appearance: none;
  appearance: none;
  pointer-events: none;
  outline: none;
}
.dual-range-input::-webkit-slider-runnable-track {
  background: transparent;
  height: 32px;
  border-radius: 6px;
}
.dual-range-input::-moz-range-track {
  background: transparent;
  height: 32px;
  border-radius: 6px;
}
.dual-range-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  pointer-events: all;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  border: 2.5px solid #555;
  cursor: grab;
  box-shadow: 0 1px 4px rgba(0,0,0,0.35);
  margin-top: 5px;
  transition: border-color 0.1s, box-shadow 0.1s;
}
.dual-range-input::-webkit-slider-thumb:active {
  cursor: grabbing;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0,123,255,0.25);
}
.dual-range-input::-moz-range-thumb {
  pointer-events: all;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 2.5px solid #555;
  cursor: grab;
  box-shadow: 0 1px 4px rgba(0,0,0,0.35);
}
.dual-range-input::-moz-range-thumb:active { cursor: grabbing; border-color: #007bff; }

/* Readout row below slider */
.slider-readout-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-bottom: 4px;
  margin-top: 6px;
}
/* Groups each tier's label/separator/input together so a 3+ band field's ≥ symbols
   stay next to the number they qualify, rather than spreading edge-to-edge across a
   wide layout via the row's flex-start + gap layout. */
.readout-group {
  display: flex;
  align-items: center;
  gap: 4px;
}
.readout-tier-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}
.readout-end {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}
.readout-tier { font-weight: 700; font-size: 12px; }
.readout-sep  { color: #888; font-size: 12px; }
.readout-unit { color: #666; font-size: 11px; }
.readout-center { display: flex; align-items: center; gap: 4px; font-size: 12px; }

/* 3-handle compiled score readout */
.compiled-readout-row { align-items: flex-start; }
.compiled-readout-row .readout-end { flex: 1; }
.compiled-readout-row .readout-center { flex: 0 0 auto; }
</style>
